import React from 'react'
import type { Placement } from '@floating-ui/react-dom'

import { Action } from './common/Action/Action'
import { Anchor } from './common/Anchor/Anchor'
import { Menu } from './common/Menu/Menu'
import { MenuItem } from './common/MenuItem/MenuItem'

import { useFloatingElement, useOnClickOutside } from 'hooks'
import { mergeRefs } from 'utils/refs'
import { DropdownHandlersContext, DropdownPropsContext } from './context'
import type { DropdownHandlersContextValue, DropdownPropsContextValue } from './context'

export { DropdownHandlersContext, DropdownPropsContext }

type DropdownProps = {
  children?: React.ReactNode
  hasArrow?: boolean
  initialPlacement?: Placement
  isInitiallyToggled?: boolean
  offsetSize?: number
}

type DropdownComponent = ((props: DropdownProps) => React.JSX.Element) & {
  Action: typeof Action
  Anchor: typeof Anchor
  Menu: typeof Menu
  MenuItem: typeof MenuItem
}

export const Dropdown: DropdownComponent = Object.assign(({
  children,
  initialPlacement,
  offsetSize,
  hasArrow = false,
  isInitiallyToggled = false,
}: DropdownProps) => {
  const [isToggled, setIsToggled] = React.useState(isInitiallyToggled)

  const floatingRef = React.useRef(null)

  const {
    arrowRef,
    arrowStyle,
    floating,
    floatingStyle,
    middlewareData,
    reference,
  } = useFloatingElement({ initialPlacement, offsetSize, hasArrow })

  const handleDismiss = React.useCallback(() => {
    setIsToggled(false)
  }, [setIsToggled])

  const handleToggle = React.useCallback(
    (event: Event | React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      event.preventDefault()

      setIsToggled(currentValue => !currentValue)
    },
    [setIsToggled],
  )

  const propsContext = React.useMemo<DropdownPropsContextValue>(
    () => ({
      arrowRef,
      arrowStyle,
      floating: mergeRefs([floatingRef, floating]),
      floatingStyle,
      middlewareData,
      reference,
      hasArrow,
      isToggled,
    }),
    [
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      middlewareData,
      reference,
      hasArrow,
      isToggled,
    ],
  )

  const handlersContext = React.useMemo<DropdownHandlersContextValue>(
    () => ({
      onDismiss: handleDismiss,
      onToggle: handleToggle,
    }),
    [handleDismiss, handleToggle],
  )

  useOnClickOutside(floatingRef, handleToggle)

  return (
    <DropdownPropsContext.Provider value={propsContext}>
      <DropdownHandlersContext.Provider value={handlersContext}>
        {children}
      </DropdownHandlersContext.Provider>
    </DropdownPropsContext.Provider>
  )
}, {
  Action,
  Anchor,
  Menu,
  MenuItem,
})
